import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { get } from 'lodash';

import { Icon, Text } from '@paypalcorp/pp-react';

import { useScreenSizeInfo } from '../ScreenSizeInfoProvider';

const TitleAndBreadCrumbs = props => {
  const {
    location: { pathname = '/appcenter' },
    flatPSData: { categories = {}, tools = {} },
    fptiClick,
    app: { context: { locale: { directionality } = {} } = {} } = {}
  } = props;

  const mainContent = {
    label: get(props, 'flatPSData.main[0].title'),
    fpti: 'main'
  };
  const categoryContent = useMemo(() => {
    return Object.values(categories).reduce((acc, category) => {
      acc[category.location] = {
        label: category.categoryTitle,
        fpti: category.categoryFPTI,
        categoryKey: category.categoryKey
      };
      return acc;
    }, {});
  }, [categories]);
  const toolContent = useMemo(() => {
    return Object.values(tools).reduce((acc, tool) => {
      acc[tool.location] = {
        label: tool.appTitle,
        fpti: tool.appFPTI,
        appKey: tool.appKey
      };
      return acc;
    }, {});
  }, [tools]);
  const [, mainSlug, categorySlug, detailsSlug] = pathname.split('/');

  const paths = [mainSlug, categorySlug, detailsSlug]
    .filter(Boolean)
    .map(slug => {
      if (slug === detailsSlug) {
        return {
          path: `/${mainSlug}/${categorySlug}/${detailsSlug}`,
          ...toolContent[detailsSlug]
        };
      } else if (slug === categorySlug) {
        return {
          path: `/${mainSlug}/${categorySlug}`,
          ...categoryContent[categorySlug]
        };
      }
      return { path: `/${slug}`, ...mainContent };
    });

  const getOnClick = ({ fpti, categoryKey, appKey }) => {
    return () => {
      // add prod <product> for details pages and category 'parent' for category pages
      fptiClick({
        fptiLink: fpti,
        prod: appKey ? toolContent[appKey]?.fpti : null,
        category: !appKey && categoryKey ? 'parent' : null
      });
    };
  };

  return (
    <section id="appcenter-title-and-bread-crumbs">
      {paths.length === 1 ? (
        <Text as="h1" size="xl2" medium>
          {mainContent.label}
        </Text>
      ) : (
        <BreadCrumbs
          getOnClick={getOnClick}
          content={paths}
          directionality={directionality}
        />
      )}
    </section>
  );
};

const BreadCrumbs = ({ content = [], getOnClick, directionality }) => {
  const { isMobile } = useScreenSizeInfo();
  if (!content.length) {
    return null;
  }
  const chevronIcon =
    directionality === 'rtl' ? 'chevron-left' : 'chevron-right';

  let renderedBreadcrumbs = content.map((item, i) => {
    const { fpti, categoryKey, appKey } = item;
    return (
      <>
        {i ? <Icon size="sm" name={chevronIcon} /> : null}
        <Text
          size="lg"
          as={Link}
          className="add-focus-visible"
          to={item.path}
          onClick={getOnClick({ fpti, categoryKey, appKey })}
        >
          {item.label}
        </Text>
      </>
    );
  });
  renderedBreadcrumbs[renderedBreadcrumbs.length - 1] = (
    <>
      <Icon size="sm" name={chevronIcon} />
      <Text
        size="lg"
        medium
        className="last add-focus-visible"
        as={Link}
        to={content[content.length - 1].path}
        onClick={getOnClick(content[content.length - 1])}
      >
        {content[content.length - 1].label}
      </Text>
    </>
  );

  if (isMobile) {
    renderedBreadcrumbs[0] = (
      <Icon
        size="xl"
        name="store"
        as={Link}
        className="add-focus-visible"
        aria-label={content[0].label}
        to={'/appcenter'}
        onClick={getOnClick(content[0])}
      />
    );
    renderedBreadcrumbs.splice(1, renderedBreadcrumbs.length - 2);
  }

  return renderedBreadcrumbs;
};

BreadCrumbs.propTypes = {
  content: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      label: PropTypes.string
    })
  ),
  directionality: PropTypes.string.isRequired
};

TitleAndBreadCrumbs.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string
  }).isRequired,
  flatPSData: PropTypes.shape({
    categories: PropTypes.object,
    tools: PropTypes.object
  }).isRequired,
  fptiClick: PropTypes.func.isRequired,
  categories: PropTypes.array,
  app: PropTypes.object.isRequired,
  tools: PropTypes.object,
  ps: PropTypes.object,
  dispatch: PropTypes.func,
  clearSearch: PropTypes.func
};

export default TitleAndBreadCrumbs;
